import React, { FC, HTMLAttributes } from 'react'
import { useParams, useHistory, useLocation } from 'react-router'
import { Card, ImageBox, Counter } from '@engyalo/storefront-components'
import { viewProduct, addProduct, removeProduct } from '@engyalo/storefront-events'
import { Hook } from '@engyalo/storefront-types'
import { Box, Heading, Skeleton } from '@chakra-ui/core'
import { Price } from '../Price'
import { ICartDictionary, IProduct, IOrder } from '@engyalo/storefront-types/lib/plugins/model'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { useRecoilState, useRecoilValue } from 'recoil'
import { cartState, configState, orderConfigState, orderState } from '../../recoil/atoms'
import Plugin from '../../Plugin'
import { generateCartId } from '../../lib/cartIdGenerator'
import { getTotalPrice } from '../../lib/getTotalPrice'
import { getActionScreen } from '../../utils/analytics'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  product: IProduct
  isLoading: boolean
  showPrice: boolean
}

export const ProductItem: FC<IProps> = ({ product, isLoading, showPrice }: IProps) => {
  const [cart, setCart] = useRecoilState<ICartDictionary>(cartState)
  const config = useRecoilValue(configState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const history = useHistory()
  const location = useLocation()
  const { customer, order } = useParams()

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
  }

  const getProductFirstImage = (product: IProduct): string => {
    if (product && product.imageURL) return product.imageURL[0] || ''
    return ''
  }

  const addToCart = (product: IProduct, adder = 1): void => {
    const tempCart = { ...cart }
    if (product && product.id) {
      const currentSku = generateCartId(product)
      if (tempCart[currentSku]) {
        const tempProd = { ...tempCart[currentSku] }
        if (tempProd.quantity) {
          const totalPrice = getTotalPrice(product)
          tempProd.quantity += adder
          if (tempProd.quantity > 0) {
            tempCart[currentSku] = {
              ...tempProd,
              totalPrice,
            }
            setCart(tempCart)
          } else {
            delete tempCart[currentSku]
            setCart({ ...tempCart })
          }
        }
      } else if (adder > 0) {
        const totalPrice = getTotalPrice(product)
        tempCart[currentSku] = {
          ...product,
          totalPrice,
          quantity: adder,
          cartId: currentSku,
        } as IProduct
      }
      setCart(tempCart)
    }
  }

  if (product) {
    return (
      <Box
        onClick={(): void => {
          viewProduct({
            product: {
              id: product.id || '',
              sku: product.sku || '',
              name: product.name || '',
              price: product.price || 0,
              category: product.category || '',
              screen: getActionScreen(location),
            },
            session,
          })
          history.push({
            pathname: `/${customer}/${order}/product`,
            state: { product },
          })
        }}
      >
        <Plugin
          hook={Hook.COLLECTION_PRODUCT}
          pluginProps={{
            product,
            currency: config.currency,
            currencyPrefix: config.currencyPrefix,
            cart,
            showPrice: showPrice,
            isLoading: isLoading,
          }}
        >
          <Card>
            <Skeleton isLoaded={!isLoading} w="100%" h={isLoading ? '130px' : 'unset'}>
              <ImageBox imageUrl={getProductFirstImage(product)} />
            </Skeleton>
            <Skeleton
              isLoaded={!isLoading}
              w={isLoading ? '40%' : 'unset'}
              h={isLoading ? '16px' : 'unset'}
              flex={isLoading ? 'unset' : '1'}
              marginY="4px"
            >
              <Heading as="h3" size="sm">
                {product.name}
              </Heading>
            </Skeleton>
            <Skeleton
              isLoaded={!isLoading}
              w={isLoading ? '100%' : 'unset'}
              h={isLoading ? '16px' : 'unset'}
              paddingBottom="4px"
            >
              <Box fontSize="xs" fontFamily="body" color="gray.100">
                {product.description}
              </Box>
            </Skeleton>

            {showPrice ? (
              <Skeleton
                isLoaded={!isLoading}
                w={isLoading ? '40%' : 'unset'}
                h={isLoading ? '16px' : 'unset'}
                flex={isLoading ? 'unset' : '1'}
                marginY="4px"
              >
                <Price price={product.price} currency={config.currencyPrefix} />
              </Skeleton>
            ) : null}

            <Skeleton isLoaded={!isLoading} h="32px" w="100%">
              <Counter
                name={product.sku || ''}
                addAction={(e): void => {
                  e.stopPropagation()
                  addProduct({
                    product: {
                      id: product.id || '',
                      sku: product.sku || '',
                      name: product.name || '',
                      price: product.price || 0,
                      category: product.category || '',
                      screen: getActionScreen(location),
                      note: '',
                    },
                    session,
                  })
                  addToCart(product, 1)
                }}
                minusAction={(e): void => {
                  e.stopPropagation()
                  removeProduct({
                    product: {
                      id: product.id || '',
                      sku: product.sku || '',
                      name: product.name || '',
                      price: product.price || 0,
                      category: product.category || '',
                      screen: getActionScreen(location),
                      note: '',
                    },
                    session,
                  })
                  addToCart(product, -1)
                }}
                value={cart[generateCartId(product) || ''] ? cart[generateCartId(product) || ''].quantity : 0}
              />
            </Skeleton>
          </Card>
        </Plugin>
      </Box>
    )
  }
  return null
}
